import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Modal, Input, Avatar, Tooltip, Spin, message, Dropdown } from 'antd';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
    faEnvelope, faPaperPlane, faFileLines, faTrash, faTrashCan,
    faPlus, faRotate, faFolder, faBoxArchive, faShareFromSquare,
    faMagnifyingGlass, faInbox, faXmark, faFlag, faThumbtack, faPaperclip,
} from '@fortawesome/free-solid-svg-icons';

import EmailThread from '../Sales1/EmailThread';
import EmailForm from '../Sales1/EmailForm';
import ReplyCard from '../Sales1/ReplyCard';
import AttachmentList from './AttachmentList';
import TrackingPanel from './TrackingPanel';
import {
    deleteEmailThunk, FetchReplysThunk, fetchInitialEmails, loadMoreEmails,
    markEmailAsRead, markEmailAsUnread, fetchMailFolders, fetchFolderOnDemand,
    pollInboxSilent,
} from '../../../Slices/Thunk/notification';
import { discardDraftThunk } from '../../../Slices/salesSlice';
import { setCurrentMail, setremoveNotification, setEmailReadStatus, toggleFlagEmail, togglePinEmail, setFolderMeta } from '../../../Slices/notification';
import { SERVER_URL } from '../../../databucket';
import socket from '../Sales1/socket';

dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(relativeTime);

// ── Constants ─────────────────────────────────────────────────────────────────

const FOLDER_ICON_MAP = {
    inbox: faEnvelope,
    'sent items': faPaperPlane,
    sentitems: faPaperPlane,
    drafts: faFileLines,
    'deleted items': faTrash,
    deleteditems: faTrash,
    'junk email': faTrashCan,
    junkemail: faTrashCan,
    outbox: faShareFromSquare,
    archive: faBoxArchive,
    clutter: faFolder,
};

const DEFAULT_FOLDER_META = [
    { id: 'Inbox',        displayName: 'Inbox',         key: 'inbox' },
    { id: 'SentItems',    displayName: 'Sent Items',    key: 'sentitems' },
    { id: 'Drafts',       displayName: 'Drafts',        key: 'drafts' },
    { id: 'DeletedItems', displayName: 'Deleted Items', key: 'deleteditems' },
    { id: 'JunkEmail',    displayName: 'Junk Email',    key: 'junkemail' },
];

function parseDevice(ua = '') {
    if (!ua) return '💻 Desktop';
    const u = ua.toLowerCase();
    if (u.includes('iphone') || u.includes('android') && u.includes('mobile')) return '📱 Mobile';
    if (u.includes('ipad') || u.includes('tablet')) return '📱 Tablet';
    if (u.includes('mac')) return '🍎 Mac';
    if (u.includes('windows')) return '🪟 Windows';
    return '💻 Desktop';
}

const AVATAR_COLORS = ['#0078d4','#107c10','#ca5010','#c50f1f','#8764b8','#038387','#e3008c','#2d7d9a'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name) {
    if (!name) return '?';
    const p = name.trim().split(/\s+/);
    return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
}

function avatarColor(name = '') {
    return AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];
}

function formatTime(dateStr) {
    if (!dateStr) return '';
    const d = dayjs(dateStr);
    if (d.isToday())     return d.format('h:mm A');
    if (d.isYesterday()) return 'Yesterday';
    if (d.isAfter(dayjs().subtract(6, 'day'))) return d.format('ddd');
    return d.format('M/D/YY');
}

function stripTrackingPixels(html) {
    if (!html) return '';
    // Remove our own tracking pixel (prevents false opens when viewing email in dashboard)
    return html.replace(/<img[^>]*\/api\/track\/open\/[^>]*\/?>/gi, '');
}

// ── EmailListItem ─────────────────────────────────────────────────────────────

const EmailListItem = React.memo(({ email, isSelected, onSelect, onDelete, onMarkRead, onMarkUnread, onFlag, onPin, isFlagged, isPinned, activeFolder }) => {
    const [hovered, setHovered] = useState(false);
    const senderInfo = email.sender?.emailAddress || email.from?.emailAddress || {};
    const name = senderInfo.name || senderInfo.address || 'Unknown';
    const isUnread = !email.isRead;
    const timeStr = formatTime(email.receivedDateTime || email.sentDateTime);
    const preview = (email.bodyPreview || '').replace(/\s+/g, ' ').slice(0, 58);

    const contextMenuItems = [
        email.isRead
            ? { key: 'unread',  label: '✉️ Mark as Unread',  onClick: (e) => { e.domEvent?.stopPropagation?.(); onMarkUnread?.(); } }
            : { key: 'read',    label: '📖 Mark as Read',     onClick: (e) => { e.domEvent?.stopPropagation?.(); onMarkRead?.(); } },
        isFlagged
            ? { key: 'unflag',  label: '🚩 Remove Flag',      onClick: (e) => { e.domEvent?.stopPropagation?.(); onFlag?.(); } }
            : { key: 'flag',    label: '🚩 Flag',             onClick: (e) => { e.domEvent?.stopPropagation?.(); onFlag?.(); } },
        isPinned
            ? { key: 'unpin',   label: '📌 Unpin',            onClick: (e) => { e.domEvent?.stopPropagation?.(); onPin?.(); } }
            : { key: 'pin',     label: '📌 Pin to top',       onClick: (e) => { e.domEvent?.stopPropagation?.(); onPin?.(); } },
        { type: 'divider' },
        { key: 'delete', label: '🗑 Delete', danger: true, onClick: (e) => { e.domEvent?.stopPropagation?.(); onDelete(); } },
    ];

    return (
        <Dropdown menu={{ items: contextMenuItems }} trigger={['contextMenu']}>
        <div
            onClick={onSelect}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 14px 10px 12px',
                cursor: 'pointer',
                background: isSelected ? '#deecf9' : hovered ? '#f3f3f3' : 'transparent',
                borderBottom: '1px solid #edebe9',
                borderLeft: isPinned ? '3px solid #8764b8' : isSelected ? '3px solid #0078d4' : '3px solid transparent',
                position: 'relative',
                minHeight: 72,
            }}
        >
            {/* Unread dot */}
            <div style={{ paddingTop: 7, width: 8, flexShrink: 0 }}>
                {isUnread && (
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#0078d4' }} />
                )}
            </div>
            {/* Avatar */}
            <Avatar size={34} style={{ background: avatarColor(name), fontSize: 12, flexShrink: 0, lineHeight: '34px', marginTop: 2 }}>
                {getInitials(name)}
            </Avatar>
            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
                {/* Row 1: Sender name + time + inline action icons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontWeight: isUnread ? 700 : 500, fontSize: 13, color: '#201f1e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {name.length > 24 ? name.slice(0, 24) + '…' : name}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        {/* Attachment indicator — shows in all folders for any email with attachments */}
                        {email.hasAttachments && (
                            <FontAwesomeIcon icon={faPaperclip} title="Has attachments" style={{ fontSize: 11, color: '#605e5c', marginRight: 4 }} />
                        )}
                        <span style={{ fontSize: 11, color: isUnread ? '#0078d4' : '#797775', fontWeight: isUnread ? 700 : 400, marginRight: 4 }}>
                            {timeStr}
                        </span>
                        {/* Read/Unread toggle */}
                        <div
                            onClick={e => { e.stopPropagation(); email.isRead ? onMarkUnread?.() : onMarkRead?.(); }}
                            title={email.isRead ? 'Mark as unread' : 'Mark as read'}
                            style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: 2, color: isUnread ? '#0078d4' : '#c8c6c4' }}
                        >
                            <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 11 }} />
                        </div>
                        {/* Flag toggle */}
                        <div
                            onClick={e => { e.stopPropagation(); onFlag?.(); }}
                            title={isFlagged ? 'Remove flag' : 'Flag'}
                            style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: 2, color: isFlagged ? '#d83b01' : '#c8c6c4' }}
                        >
                            <FontAwesomeIcon icon={faFlag} style={{ fontSize: 11 }} />
                        </div>
                        {/* Pin toggle */}
                        <div
                            onClick={e => { e.stopPropagation(); onPin?.(); }}
                            title={isPinned ? 'Unpin' : 'Pin to top'}
                            style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: 2, color: isPinned ? '#8764b8' : '#c8c6c4' }}
                        >
                            <FontAwesomeIcon icon={faThumbtack} style={{ fontSize: 11 }} />
                        </div>
                        {/* Delete — hover only */}
                        {hovered && activeFolder !== 'deleteditems' && (
                            <div
                                onClick={e => { e.stopPropagation(); onDelete(); }}
                                title="Delete"
                                style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: 2, color: '#a4262c' }}
                            >
                                <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11 }} />
                            </div>
                        )}
                    </div>
                </div>
                {/* Row 2: Subject */}
                <div style={{ fontSize: 12, color: '#323130', fontWeight: isUnread ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                    {email.subject || '(No subject)'}
                </div>
                {/* Row 3: Preview */}
                <div style={{ fontSize: 11, color: '#797775', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                    {preview}
                    {email.provider === 'ses' && (
                        <span style={{ marginLeft: 6, fontSize: 9, color: '#0078d4', fontWeight: 700, background: '#deecf9', padding: '1px 4px', borderRadius: 3 }}>
                            SES
                        </span>
                    )}
                    {email.emailType === 'reply' && (
                        <span style={{ marginLeft: 4, fontSize: 9, color: '#6366f1', fontWeight: 700, background: '#f0effe', padding: '1px 4px', borderRadius: 3 }}>
                            Reply
                        </span>
                    )}
                </div>
            </div>
        </div>
        </Dropdown>
    );
});

// ── EmptyPane ─────────────────────────────────────────────────────────────────

const EmptyPane = ({ label = 'Nothing to see here' }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, color: '#797775' }}>
        <FontAwesomeIcon icon={faInbox} style={{ fontSize: 48, color: '#c8c6c4' }} />
        <span style={{ fontSize: 15, fontWeight: 600, color: '#605e5c' }}>{label}</span>
        <span style={{ fontSize: 13, color: '#a19f9d' }}>Select an item to read</span>
    </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const OutlookFolders = ({ EmailsLists, mode, embedded }) => {
    const dispatch = useDispatch();
    const [activeFolder, setActiveFolder]     = useState('inbox');
    const [selectedEmail, setSelectedEmail]   = useState(null);
    const [response, setResponse]             = useState([]);
    const [emailOpen, setEmailOpen]           = useState({});
    const [tab, setTab]                       = useState(false);
    const [emailTab, setEmailTab]             = useState('focused');
    const [refreshing, setRefreshing]         = useState(false);
    const [noThreadCompose, setNoThreadCompose] = useState(null); // { replyData, forwardData }
    const [searchQuery, setSearchQuery]       = useState('');
    const [folderLoading, setFolderLoading]   = useState(false);
    const [leadDrafts, setLeadDrafts]         = useState([]); // drafts from leadsEmails for this email account
    const [leadDraftsLoading, setLeadDraftsLoading] = useState(false);
    const [outlookDraftEdit, setOutlookDraftEdit] = useState(null); // Outlook draft being edited in compose
    const [editingLeadDraft, setEditingLeadDraft] = useState(null); // lead draft being edited from Drafts folder
    const [attachments, setAttachments] = useState([]); // attachments for the currently selected email
    const [attachmentsLoading, setAttachmentsLoading] = useState(false);

    const dbdata      = useSelector(s => s.EmailData.byUser);
    const openWindow  = useSelector(s => s.EmailData.openWindow);
    const email       = useSelector(s => s.EmailData.mail);
    const flaggedMap  = useSelector(s => s.EmailData.flagged || {});
    const pinnedMap   = useSelector(s => s.EmailData.pinned  || {});
    const emailRef    = useRef(email);
    const containerRef = useRef(null);

    useEffect(() => { emailRef.current = email; }, [email]);

    // Fetch attachments when an email with hasAttachments=true is opened.
    // Graph emails: fetch via /api/outlook/attachments (returns full content for download).
    // SES emails: metadata is inlined on the email object (name/contentType/size only — no contentBytes).
    // Important: every non-Graph branch must explicitly setAttachmentsLoading(false), otherwise a
    // previous Graph email's in-flight fetch (cancelled by cleanup) can leave the loader stuck on.
    useEffect(() => {
        let cancelled = false;
        const acct = selectedEmail?._account || email;
        if (!selectedEmail || !selectedEmail.hasAttachments) {
            setAttachments([]);
            setAttachmentsLoading(false);
            return;
        }
        // SES path: use inline metadata. Download button stays disabled (no contentBytes stored).
        if (selectedEmail.provider === 'ses') {
            setAttachments(selectedEmail.attachments || []);
            setAttachmentsLoading(false);
            return;
        }
        // Graph path needs a real account; skip in __all__ until an individual email is selected with its _account.
        if (!acct || acct === '__all__') {
            setAttachments([]);
            setAttachmentsLoading(false);
            return;
        }
        setAttachments([]); // clear stale attachments from a previous email while the new fetch runs
        setAttachmentsLoading(true);
        axios.get(`${SERVER_URL}/api/outlook/attachments`, {
            params: { email: acct, messageId: selectedEmail.id },
        })
            .then(res => { if (!cancelled) setAttachments(res.data || []); })
            .catch(() => { if (!cancelled) setAttachments([]); })
            .finally(() => { if (!cancelled) setAttachmentsLoading(false); });
        return () => { cancelled = true; };
    }, [selectedEmail?.id, selectedEmail?.hasAttachments, selectedEmail?.provider, selectedEmail?._account, email]);

    // Fetch lead drafts for the current email account when Drafts folder is opened
    useEffect(() => {
        if (activeFolder !== 'drafts' || !email || email === '__all__') {
            setLeadDrafts([]);
            return;
        }
        let cancelled = false;
        setLeadDraftsLoading(true);
        // Lightweight GraphQL query: get all leads, filter locally for isDraft entries matching this account
        axios.post(`${SERVER_URL}/graphql`, {
            query: `query {
                getAllLeads {
                    id
                    agencyName
                    leadsEmails {
                        id emailFrom emailedto emailedy emailBody body isDraft draftSavedAt
                    }
                }
            }`,
        }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
            .then(res => {
                if (cancelled) return;
                const leads = res.data?.data?.getAllLeads || [];
                const drafts = [];
                leads.forEach(lead => {
                    (lead.leadsEmails || []).forEach(entry => {
                        if (entry.isDraft && entry.emailFrom === email) {
                            drafts.push({ ...entry, leadId: lead.id, agencyName: lead.agencyName });
                        }
                    });
                });
                drafts.sort((a, b) => new Date(b.draftSavedAt || 0) - new Date(a.draftSavedAt || 0));
                setLeadDrafts(drafts);
            })
            .catch(() => { if (!cancelled) setLeadDrafts([]); })
            .finally(() => { if (!cancelled) setLeadDraftsLoading(false); });
        return () => { cancelled = true; };
    }, [activeFolder, email]); // eslint-disable-line react-hooks/exhaustive-deps

    // When compose form closes, silently re-fetch the Drafts folder so the draft
    // appears in the list and the count badge updates
    useEffect(() => {
        if (tab || isAll || !email) return;
        const draftsMeta = (dbdata[email]?.folderMeta || []).find(f => f.key === 'drafts');
        if (draftsMeta?.id) {
            dispatch(fetchFolderOnDemand({ email, folderId: draftsMeta.id, folderKey: 'drafts', silent: true }));
        }
    }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

    const isAll       = email === '__all__';
    const allAccounts = EmailsLists || [];

    // Merge Graph folders with defaults to always show standard folders
    const metaSource = isAll ? allAccounts[0] : email;
    const graphFolders = (metaSource && dbdata[metaSource]?.folderMeta) || [];
    const folderMeta = graphFolders.length > 0 ? (() => {
        // Start with defaults, then merge in Graph folders (adds custom ones, preserves standard)
        const defaultKeys = new Set(DEFAULT_FOLDER_META.map(f => f.key));
        const graphMap = new Map(graphFolders.map(f => [f.key, f]));
        const merged = DEFAULT_FOLDER_META.map(def => graphMap.get(def.key) || def);
        const extras = graphFolders.filter(f => !defaultKeys.has(f.key));
        return [...merged, ...extras];
    })() : DEFAULT_FOLDER_META;

    // Emails for current folder. In "All Emails" view, tag each email with its source mailbox
    // (_account) so per-email actions (mark read, flag, delete, fetch thread) can target the
    // correct MS Graph mailbox instead of the meaningless "__all__" selection.
    const folderEmails = isAll
        ? allAccounts
            .flatMap(acc => (dbdata[acc]?.folders?.[activeFolder]?.data || []).map(e => ({ ...e, _account: acc })))
            .sort((a, b) => new Date(b.sentDateTime || b.receivedDateTime) - new Date(a.sentDateTime || a.receivedDateTime))
        : dbdata[email]?.folders?.[activeFolder]?.data || [];

    // Deduplicate by conversationId
    const seenIds = new Set();
    const dedupedEmails = (folderEmails || []).filter(e => {
        const id = e.conversationId?.trim() || e.id;
        if (id && seenIds.has(id)) return false;
        if (id) seenIds.add(id);
        return true;
    });

    // Filter by inbox tabs
    const showTabs = activeFolder === 'inbox';
    const tabFiltered = showTabs
        ? dedupedEmails.filter(e => e.inferenceClassification === emailTab)
        : dedupedEmails;

    // Search filter
    const filteredEmails = searchQuery.trim()
        ? tabFiltered.filter(e => {
            const q = searchQuery.toLowerCase();
            const name = (e.sender?.emailAddress?.name || e.from?.emailAddress?.name || '').toLowerCase();
            const subj = (e.subject || '').toLowerCase();
            const prev = (e.bodyPreview || '').toLowerCase();
            return name.includes(q) || subj.includes(q) || prev.includes(q);
        })
        : tabFiltered;

    // Flag / pin sets for current user
    const flaggedSet = useMemo(() => new Set(flaggedMap[email] || []), [flaggedMap, email]);
    const pinnedSet  = useMemo(() => new Set(pinnedMap[email]  || []), [pinnedMap,  email]);

    // Pinned emails bubble to top
    const sortedEmails = useMemo(() => {
        if (pinnedSet.size === 0) return filteredEmails;
        return [...filteredEmails].sort((a, b) => {
            const ap = pinnedSet.has(a.conversationId);
            const bp = pinnedSet.has(b.conversationId);
            return ap === bp ? 0 : ap ? -1 : 1;
        });
    }, [filteredEmails, pinnedSet]);

    // Email selector for compose form
    const EmailFormList = mode
        ? isAll
            ? allAccounts.map((em, i) => ({ key: i, value: em, label: em }))
            : [{ key: 0, value: email, label: email }]
        : [
            ...(mode ? [{ key: '__all__', value: '__all__', label: 'All Emails' }] : []),
            ...(EmailsLists?.map((em, i) => ({ key: i, value: em, label: em })) || []),
          ];

    const EmailList = [
        ...(mode ? [{ key: '__all__', value: '__all__', label: 'All Emails' }] : []),
        ...(EmailsLists?.map((em, i) => ({ key: i, value: em, label: em })) || []),
    ];

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        // Pre-populate folderMeta from localStorage so folders appear immediately on refresh,
        // before the Graph API call completes (which may take 1-2s or fail intermittently).
        const targets = isAll ? allAccounts : (email ? [email] : []);
        targets.forEach(acc => {
            try {
                const cached = localStorage.getItem(`email_folder_meta_${acc}`);
                if (cached) dispatch(setFolderMeta({ user: acc, folderMeta: JSON.parse(cached) }));
            } catch {}
        });

        if (isAll) {
            allAccounts.forEach(acc => {
                dispatch(setremoveNotification(acc));
                dispatch(fetchInitialEmails(acc));
            });
            if (allAccounts[0]) dispatch(fetchMailFolders(allAccounts[0]));
        } else if (email) {
            dispatch(setremoveNotification(email));
            dispatch(fetchInitialEmails(email));
            dispatch(fetchMailFolders(email));
        }
    }, [email]); // eslint-disable-line

    // Poll every 60s — inbox only, silently, so other folder views are not reset.
    // fetchInitialEmails calls setAllFolderData which replaces ALL folder state;
    // pollInboxSilent only updates inbox via initFolderInState.
    useEffect(() => {
        const targets = isAll ? allAccounts : (email ? [email] : []);
        if (targets.length === 0) return;
        const interval = setInterval(() => {
            targets.forEach(acc => dispatch(pollInboxSilent(acc)));
        }, 60000);
        return () => clearInterval(interval);
    }, [email]); // eslint-disable-line

    // Infinite scroll
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const user = emailRef.current;
            const folder = activeFolder;
            const skip = dbdata[user]?.folders?.[folder]?.skip || 0;
            const hasMore = dbdata[user]?.folders?.[folder]?.hasMore ?? true;
            if (scrollTop + clientHeight >= scrollHeight - 40 && hasMore) {
                dispatch(loadMoreEmails({ user, folder, skip }));
            }
        };
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [containerRef, dbdata, activeFolder]); // eslint-disable-line

    // ── Single-email card adapter (for non-thread emails) ─────────────────────
    const singleEmailReply = useMemo(() => {
        if (!selectedEmail || !emailOpen?.messageId) return null;
        return {
            id: selectedEmail.id,
            from: selectedEmail.sender?.emailAddress?.address || selectedEmail.from?.emailAddress?.address || '',
            fromName: selectedEmail.sender?.emailAddress?.name || selectedEmail.from?.emailAddress?.name || '',
            to: selectedEmail.toRecipients?.map(r => r.emailAddress?.address || '').join(', ') || '',
            toNames: selectedEmail.toRecipients?.map(r => r.emailAddress?.name || r.emailAddress?.address || '').join(', ') || '',
            ccRecipients: selectedEmail.ccRecipients?.length
                ? selectedEmail.ccRecipients.map(r => r.emailAddress?.address || '').join(', ')
                : undefined,
            subject: selectedEmail.subject || '',
            bodyPreview: emailOpen.content || selectedEmail.body?.content || selectedEmail.bodyPreview || '',
            receivedDateTime: selectedEmail.receivedDateTime || selectedEmail.sentDateTime,
            attachments: selectedEmail.attachments,
            flag: selectedEmail.flag,
            provider: selectedEmail.provider,
        };
    }, [selectedEmail, emailOpen]);

    // Handlers for single-email card compose
    const handleSingleReply = useCallback((reply) => {
        const from = email;
        const senderAddr = selectedEmail?.sender?.emailAddress?.address || selectedEmail?.from?.emailAddress?.address || '';
        const toAddr = senderAddr === from
            ? (selectedEmail?.toRecipients?.[0]?.emailAddress?.address || '')
            : senderAddr;
        dispatch(setCurrentMail(from));
        setNoThreadCompose({
            replyData: { messageId: reply.id, to: toAddr, subject: reply.subject || '', body: emailOpen.content || '', from, date: reply.receivedDateTime },
            forwardData: null,
        });
    }, [email, selectedEmail, emailOpen, dispatch]);

    const handleSingleReplyAll = useCallback((reply) => {
        const from = email;
        // Standard Gmail/Outlook Reply All:
        // TO: original sender only
        const senderAddr = selectedEmail?.sender?.emailAddress?.address || selectedEmail?.from?.emailAddress?.address || '';
        const toAll = senderAddr && senderAddr !== from ? senderAddr : '';
        // CC: (original TO + original CC) excluding self and the sender
        const ccAddrs = [
            ...(selectedEmail?.toRecipients?.map(r => r.emailAddress?.address) || []),
            ...(selectedEmail?.ccRecipients?.map(r => r.emailAddress?.address) || []),
        ];
        const ccAll = [...new Set(ccAddrs.filter(a => a && a !== from && a !== senderAddr))].join(', ');
        dispatch(setCurrentMail(from));
        setNoThreadCompose({
            replyData: { messageId: reply.id, to: toAll, cc: ccAll, subject: reply.subject || '', body: emailOpen.content || '', from, date: reply.receivedDateTime },
            forwardData: null,
        });
    }, [email, selectedEmail, emailOpen, dispatch]);

    const handleSingleForward = useCallback((reply) => {
        setNoThreadCompose({
            replyData: null,
            forwardData: { subject: `Fwd: ${reply.subject || ''}`, body: emailOpen.content || '', from: email, date: reply.receivedDateTime, messageId: reply.id },
        });
    }, [email, emailOpen]);

    const handleSingleDelete = useCallback(() => {
        if (!selectedEmail) return;
        handleDelete(selectedEmail);
    }, [selectedEmail]); // eslint-disable-line

    const handleSingleMarkUnread = useCallback(() => {
        if (!selectedEmail) return;
        const acct = selectedEmail._account || emailRef.current;
        if (selectedEmail.provider === 'ses') {
            dispatch(setEmailReadStatus({ user: acct, folder: activeFolder, conversationId: selectedEmail.conversationId, isRead: false }));
        } else {
            dispatch(markEmailAsUnread({ user: acct, folder: activeFolder, messageId: selectedEmail.id, conversationId: selectedEmail.conversationId }));
        }
    }, [selectedEmail, activeFolder, dispatch]);

    const handleSingleFlag = useCallback(() => {
        if (!selectedEmail) return;
        const acct = selectedEmail._account || emailRef.current;
        dispatch(toggleFlagEmail({ user: acct, conversationId: selectedEmail.conversationId }));
    }, [selectedEmail, dispatch]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    // Ref that holds the latest thread-fetch request token. Each call to handleFetchData increments
    // it; when an async response returns, we ignore it if the token has since changed — prevents a
    // late-arriving fetch from an older selection from overwriting the current selection's content.
    const fetchTokenRef = useRef(0);

    const handleFetchData = useCallback(async (messageId, _, fullEmailObject) => {
        const fallbackContent = fullEmailObject?.body?.content || fullEmailObject?.bodyPreview || '<p>No content</p>';
        const acct = fullEmailObject?._account || emailRef.current;
        // Take a new token for this request. Increment ensures uniqueness even if the same email
        // is clicked twice in a row (both requests get distinct tokens).
        const token = ++fetchTokenRef.current;
        try {
            const res = await dispatch(FetchReplysThunk({ email: acct, messageId })).unwrap();
            // STALE-GUARD: if another email was selected while this fetch was in flight, drop the result.
            if (fetchTokenRef.current !== token) return;
            const { replies } = res;
            setResponse(replies);
            setEmailOpen({
                messageId,
                email: acct,
                content: fallbackContent,
            });
        } catch {
            // STALE-GUARD: same on failure path — don't clobber the current email's content.
            if (fetchTokenRef.current !== token) return;
            // Still show the email body even if thread fetch failed
            setEmailOpen({
                messageId,
                email: acct,
                content: fallbackContent,
            });
        }
    }, [dispatch]);

    const handleEmailClick = useCallback(async (mail) => {
        const acct = mail._account || emailRef.current;
        // Drafts folder: open in compose mode for editing instead of reading pane
        if (activeFolder === 'drafts') {
            const toAddr = (mail.toRecipients || []).map(r => r.emailAddress?.address || '').filter(Boolean).join(', ');
            const ccAddr = (mail.ccRecipients  || []).map(r => r.emailAddress?.address || '').filter(Boolean).join(', ');
            const bccAddr = (mail.bccRecipients || []).map(r => r.emailAddress?.address || '').filter(Boolean).join(', ');
            setOutlookDraftEdit({
                id:      mail.id,
                to:      toAddr,
                cc:      ccAddr,
                bcc:     bccAddr,
                subject: mail.subject || '',
                body:    mail.body?.content || '',
                from:    mail.from?.emailAddress?.address || acct || '',
            });
            setTab(true);
            setSelectedEmail(null);
            return;
        }

        setSelectedEmail(mail);
        setTab(false);
        setResponse([]);
        // Pre-populate with cached body so reading pane shows immediately
        setEmailOpen({
            messageId: mail?.internetMessageId,
            email: acct,
            content: mail?.body?.content || mail?.bodyPreview || '',
        });

        // SES emails have no Graph thread — show body directly
        if (mail.provider === 'ses') {
            setEmailOpen({
                messageId: mail.id,
                email: acct,
                content: mail.body?.content || mail.bodyPreview || '',
            });
            if (!mail.isRead) {
                dispatch(setEmailReadStatus({
                    user: acct,
                    folder: activeFolder,
                    conversationId: mail.conversationId,
                    isRead: true,
                }));
            }
            return;
        }

        handleFetchData(mail?.internetMessageId, mail?.sender?.emailAddress?.address, mail);
        if (!mail.isRead) {
            // Optimistic update — mark read in UI immediately
            dispatch(setEmailReadStatus({
                user: acct,
                folder: activeFolder,
                conversationId: mail.conversationId,
                isRead: true,
            }));
            dispatch(markEmailAsRead({
                user: acct,
                folder: activeFolder,
                messageId: mail.id,
                conversationId: mail.conversationId,
            }));
        }
    }, [dispatch, activeFolder, handleFetchData]);

    const handleDelete = useCallback(async (emailItem) => {
        const result = await Swal.fire({
            title: 'Delete email?',
            text: 'This cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#c50f1f',
            cancelButtonColor: '#605e5c',
            confirmButtonText: 'Delete',
        });
        if (!result.isConfirmed) return;
        const acct = emailItem._account || emailRef.current;
        const actionResult = await dispatch(deleteEmailThunk({ user: acct, email: emailItem, activeFolder }));
        if (deleteEmailThunk.fulfilled.match(actionResult)) {
            if (selectedEmail?.id === emailItem.id) {
                // Bump the fetch token so any in-flight thread fetch for the deleted email
                // can't write back stale content into response/emailOpen.
                fetchTokenRef.current++;
                setSelectedEmail(null);
                setResponse([]);
                setEmailOpen({});
            }
            message.success('Deleted');
        } else {
            message.error('Failed to delete. Please try again.');
        }
    }, [dispatch, activeFolder, selectedEmail]);

    const handleFolderClick = useCallback((folderKey, folderId) => {
        // Invalidate any in-flight thread fetch — folder switch means user no longer cares
        // about the previously selected email's content.
        fetchTokenRef.current++;
        setActiveFolder(folderKey);
        setSelectedEmail(null);
        setResponse([]);
        setEmailOpen({});
        setSearchQuery('');
        setTab(false);              // close compose form when switching folders
        setOutlookDraftEdit(null);  // clear any Outlook draft being edited
        setEditingLeadDraft(null);  // clear any lead draft being edited
        if (!isAll) {
            const hasData = (dbdata[email]?.folders?.[folderKey]?.data?.length || 0) > 0;
            // Show syncing indicator so user knows a fresh load is happening
            setFolderLoading(true);
            dispatch(fetchFolderOnDemand({ email, folderId, folderKey, silent: hasData }))
                .finally(() => setFolderLoading(false));
        }
    }, [isAll, email, dispatch, dbdata]);

    const handleAccountSwitch = useCallback((val) => {
        // Invalidate any in-flight thread fetch — switching accounts is a hard reset.
        fetchTokenRef.current++;
        dispatch(setCurrentMail(val));
        setSelectedEmail(null);
        setResponse([]);
        setEmailOpen({});
        setSearchQuery('');
        if (containerRef.current) containerRef.current.scrollTop = 0;
    }, [dispatch]);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div style={embedded ? {
            display: 'flex',
            height: '100%',
            width: '100%',
            overflow: 'hidden',
            fontFamily: "'Segoe UI', system-ui, sans-serif",
            background: '#fff',
        } : {
            display: 'flex',
            height: '100vh',
            width: '100%',
            overflow: 'hidden',
            position: 'fixed',
            marginTop: '-52px',
            fontFamily: "'Segoe UI', system-ui, sans-serif",
            background: '#fff',
        }}>

            {/* ── LEFT: Folder sidebar ────────────────────────────────────── */}
            <div style={{
                width: 220,
                background: '#faf9f8',
                borderRight: '1px solid #edebe9',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                {/* Action buttons */}
                <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button
                        onClick={() => { setOutlookDraftEdit(null); setEditingLeadDraft(null); setTab(true); setSelectedEmail(null); }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '7px 12px',
                            background: '#fff',
                            border: '1px solid #d2d0ce',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 13,
                            color: '#323130',
                            width: '100%',
                            fontFamily: 'inherit',
                            transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f3f3f3'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                        <FontAwesomeIcon icon={faPlus} style={{ color: '#0078d4', fontSize: 13 }} />
                        New message
                    </button>
                    <button
                        onClick={async () => {
                            setRefreshing(true);
                            const targets = isAll ? allAccounts : (email ? [email] : []);
                            await Promise.all(targets.map(acc => dispatch(fetchInitialEmails(acc))));
                            setRefreshing(false);
                        }}
                        disabled={refreshing}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '6px 12px',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: 4,
                            cursor: refreshing ? 'default' : 'pointer',
                            fontSize: 12,
                            color: '#605e5c',
                            width: '100%',
                            fontFamily: 'inherit',
                        }}
                    >
                        <FontAwesomeIcon icon={faRotate} spin={refreshing} style={{ fontSize: 12 }} />
                        {refreshing ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: '#edebe9', margin: '0 10px 4px' }} />

                {/* Folder list */}
                <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
                    {folderMeta.map(({ id, displayName, key, unreadItemCount }) => {
                        const icon = FOLDER_ICON_MAP[key] || FOLDER_ICON_MAP[displayName.toLowerCase()] || faFolder;
                        const isActive = activeFolder === key;
                        // Use local email data for live count; fall back to Graph's count
                        // Drafts/Sent/Deleted show total items; Inbox/others show unread
                        const localData = !isAll ? dbdata[email]?.folders?.[key]?.data : null;
                        // Deduplicate by conversationId to match what the list actually displays
                        const dedupedLocal = localData != null ? (() => {
                            const seen = new Set();
                            return localData.filter(e => {
                                const key = e.conversationId?.trim() || e.id;
                                if (!key || seen.has(key)) return false;
                                seen.add(key);
                                return true;
                            });
                        })() : null;
                        const totalLocal = dedupedLocal?.length ?? null;
                        const unreadLocal = dedupedLocal != null ? dedupedLocal.filter(e => !e.isRead).length : null;
                        const showTotal = ['drafts', 'deleteditems'].includes(key);
                        const unread = key === 'sentitems' ? 0 : showTotal
                            ? (totalLocal ?? unreadItemCount ?? 0)
                            : (unreadLocal ?? unreadItemCount ?? 0);
                        return (
                            <div
                                key={id}
                                onClick={() => handleFolderClick(key, id)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '7px 12px',
                                    cursor: 'pointer',
                                    background: isActive ? '#e8e8e8' : 'transparent',
                                    borderLeft: isActive ? '3px solid #0078d4' : '3px solid transparent',
                                    color: isActive ? '#0078d4' : '#323130',
                                    fontSize: 13,
                                    fontWeight: unread > 0 ? 600 : 400,
                                    userSelect: 'none',
                                    borderRadius: '0 4px 4px 0',
                                    transition: 'background 0.1s',
                                }}
                                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f3f3f3'; }}
                                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <FontAwesomeIcon
                                    icon={icon}
                                    style={{ fontSize: 14, color: isActive ? '#0078d4' : '#605e5c', width: 16, flexShrink: 0 }}
                                />
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {displayName}
                                </span>
                                {unread > 0 && (
                                    <span style={{ fontSize: 11, color: '#0078d4', fontWeight: 700, flexShrink: 0 }}>
                                        {unread > 99 ? '99+' : unread}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── MIDDLE: Email list ──────────────────────────────────────── */}
            <div style={{
                width: 320,
                borderRight: '1px solid #edebe9',
                display: 'flex',
                flexDirection: 'column',
                background: '#fff',
                flexShrink: 0,
            }}>
                {/* Folder header */}
                <div style={{ padding: '10px 14px 4px', borderBottom: '1px solid #edebe9' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#201f1e', marginBottom: 6 }}>
                        {folderMeta.find(f => f.key === activeFolder)?.displayName || activeFolder}
                    </div>
                    {/* Search */}
                    <Input
                        prefix={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ fontSize: 11, color: '#a19f9d' }} />}
                        suffix={searchQuery ? (
                            <FontAwesomeIcon icon={faXmark} style={{ fontSize: 11, cursor: 'pointer', color: '#605e5c' }} onClick={() => setSearchQuery('')} />
                        ) : null}
                        placeholder="Search"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        style={{ borderRadius: 4, fontSize: 12, height: 28 }}
                        bordered
                    />
                </div>

                {/* Inbox Focused/Other tabs */}
                {showTabs && (
                    <div style={{ display: 'flex', borderBottom: '1px solid #edebe9' }}>
                        {['focused', 'other'].map(t => (
                            <div
                                key={t}
                                onClick={() => setEmailTab(t)}
                                style={{
                                    flex: 1,
                                    textAlign: 'center',
                                    padding: '6px',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    fontWeight: emailTab === t ? 600 : 400,
                                    color: emailTab === t ? '#0078d4' : '#605e5c',
                                    borderBottom: emailTab === t ? '2px solid #0078d4' : '2px solid transparent',
                                    background: '#fff',
                                    userSelect: 'none',
                                    transition: 'color 0.1s',
                                }}
                            >
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                            </div>
                        ))}
                    </div>
                )}

                {/* Syncing indicator — visible while fetchFolderOnDemand is in flight */}
                {folderLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 14px', fontSize: 11, color: '#0078d4', background: '#f0f6fc', borderBottom: '1px solid #c7e0f4' }}>
                        <Spin size="small" />
                        <span>Syncing...</span>
                    </div>
                )}

                {/* ── Lead Drafts section (Drafts folder only) ─────────────── */}
                {activeFolder === 'drafts' && (leadDrafts.length > 0 || leadDraftsLoading) && (
                    <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 8, marginBottom: 0 }}>
                        <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <FontAwesomeIcon icon={faFileLines} style={{ color: '#d97706' }} />
                            Lead Drafts ({leadDraftsLoading ? '…' : leadDrafts.length})
                        </div>
                        {leadDraftsLoading ? (
                            <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, color: '#6b7280', fontSize: 12 }}>
                                <Spin size="small" /> Loading lead drafts…
                            </div>
                        ) : leadDrafts.map(draft => (
                            <div key={draft.id}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid #fef3c7', background: '#fffbeb', cursor: 'default' }}
                            >
                                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <FontAwesomeIcon icon={faFileLines} style={{ color: '#d97706', fontSize: 13 }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', background: '#fde68a', borderRadius: 4, padding: '1px 6px', display: 'inline-block', marginBottom: 2 }}>
                                        Draft · {draft.agencyName || 'Lead'}
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {draft.emailBody || '(No subject)'}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#64748b' }}>
                                        {draft.emailedto ? `To: ${draft.emailedto}` : ''}
                                        {draft.draftSavedAt ? ` · Saved ${dayjs(draft.draftSavedAt).fromNow()}` : ''}
                                    </div>
                                </div>
                                <Tooltip title="Edit this draft">
                                    <div
                                        onClick={() => {
                                            setEditingLeadDraft(draft);
                                            setOutlookDraftEdit(null);
                                            setTab(true);
                                            setSelectedEmail(null);
                                        }}
                                        style={{ padding: '4px 6px', color: '#0078d4', cursor: 'pointer', borderRadius: 4, fontSize: 12, marginRight: 2 }}
                                    >
                                        ✏️
                                    </div>
                                </Tooltip>
                                <Tooltip title="Discard this draft permanently">
                                    <div
                                        onClick={() => {
                                            Modal.confirm({
                                                title: 'Discard draft?',
                                                content: 'This lead draft will be permanently deleted.',
                                                okText: 'Discard', okType: 'danger',
                                                cancelText: 'Cancel', centered: true,
                                                onOk: async () => {
                                                    try {
                                                        await dispatch(discardDraftThunk({ leadId: draft.leadId, draftId: draft.id })).unwrap();
                                                        setLeadDrafts(prev => prev.filter(d => d.id !== draft.id));
                                                        message.success('Draft discarded');
                                                    } catch {
                                                        message.error('Failed to discard draft');
                                                    }
                                                },
                                            });
                                        }}
                                        style={{ padding: '4px 6px', color: '#ef4444', cursor: 'pointer', borderRadius: 4, fontSize: 12 }}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </div>
                                </Tooltip>
                            </div>
                        ))}
                    </div>
                )}

                {/* Email list */}
                <div ref={containerRef} style={{ flex: 1, overflowY: 'auto' }}>
                    {sortedEmails.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, color: '#797775', gap: 8 }}>
                            <FontAwesomeIcon icon={faInbox} style={{ fontSize: 32, color: '#c8c6c4' }} />
                            <span style={{ fontSize: 13 }}>
                                {searchQuery ? 'No results' : 'Nothing here'}
                            </span>
                        </div>
                    ) : (
                        sortedEmails.map(item => (
                            <EmailListItem
                                key={item.id}
                                email={item}
                                isSelected={selectedEmail?.id === item.id}
                                isFlagged={flaggedSet.has(item.conversationId)}
                                isPinned={pinnedSet.has(item.conversationId)}
                                onSelect={() => handleEmailClick(item)}
                                onDelete={() => handleDelete(item)}
                                onFlag={() => dispatch(toggleFlagEmail({ user: item._account || emailRef.current, conversationId: item.conversationId }))}
                                onPin={() => dispatch(togglePinEmail({ user: item._account || emailRef.current, conversationId: item.conversationId }))}
                                onMarkRead={() => {
                                    const acct = item._account || emailRef.current;
                                    if (item.provider === 'ses') {
                                        dispatch(setEmailReadStatus({ user: acct, folder: activeFolder, conversationId: item.conversationId, isRead: true }));
                                    } else {
                                        dispatch(setEmailReadStatus({ user: acct, folder: activeFolder, conversationId: item.conversationId, isRead: true }));
                                        dispatch(markEmailAsRead({ user: acct, folder: activeFolder, messageId: item.id, conversationId: item.conversationId }));
                                    }
                                }}
                                onMarkUnread={() => {
                                    const acct = item._account || emailRef.current;
                                    if (item.provider === 'ses') {
                                        dispatch(setEmailReadStatus({ user: acct, folder: activeFolder, conversationId: item.conversationId, isRead: false }));
                                    } else {
                                        dispatch(markEmailAsUnread({ user: acct, folder: activeFolder, messageId: item.id, conversationId: item.conversationId }));
                                    }
                                }}
                                activeFolder={activeFolder}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* ── RIGHT: Reading pane ─────────────────────────────────────── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden', minWidth: 0 }}>
                {tab ? (
                    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                        <EmailForm
                            EmailList={EmailFormList}
                            setTab={(val) => { setTab(val); if (!val) { setOutlookDraftEdit(null); setEditingLeadDraft(null); } }}
                            outlookDraftData={outlookDraftEdit}
                            salesInfo={editingLeadDraft ? { id: editingLeadDraft.leadId, agencyName: editingLeadDraft.agencyName } : undefined}
                            draftData={editingLeadDraft ? {
                                id:        editingLeadDraft.id,
                                emailedto: editingLeadDraft.emailedto,
                                emailedy:  editingLeadDraft.emailedy,
                                remarks:   editingLeadDraft.remarks,
                                emailBody: editingLeadDraft.emailBody,
                                body:      editingLeadDraft.body,
                                emailFrom: editingLeadDraft.emailFrom,
                            } : undefined}
                            onDraftSaved={(emails) => {
                                // Keep local lead drafts list in sync
                                if (editingLeadDraft) setLeadDrafts(prev => prev.map(d => d.id === editingLeadDraft.id ? { ...d, ...emails.find(e => e.id === editingLeadDraft.id) } : d));
                            }}
                            onDraftDiscarded={(draftId) => {
                                setLeadDrafts(prev => prev.filter(d => d.id !== draftId));
                                setEditingLeadDraft(null);
                            }}
                        />
                    </div>
                ) : selectedEmail ? (
                    <>
                        {/* Email header */}
                        <div style={{ padding: '16px 24px 12px', borderBottom: '1px solid #edebe9', flexShrink: 0 }}>
                            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#201f1e', margin: '0 0 10px', lineHeight: 1.3 }}>
                                {selectedEmail.subject || '(No subject)'}
                            </h2>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Avatar
                                    size={38}
                                    style={{ background: avatarColor(selectedEmail.sender?.emailAddress?.name || ''), fontSize: 14, flexShrink: 0 }}
                                >
                                    {getInitials(selectedEmail.sender?.emailAddress?.name || '')}
                                </Avatar>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: 13, color: '#201f1e' }}>
                                        {selectedEmail.sender?.emailAddress?.name || selectedEmail.from?.emailAddress?.name || 'Unknown'}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#605e5c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {selectedEmail.sender?.emailAddress?.address || selectedEmail.from?.emailAddress?.address || ''}
                                    </div>
                                </div>
                                <div style={{ fontSize: 12, color: '#797775', flexShrink: 0 }}>
                                    {dayjs(selectedEmail.receivedDateTime || selectedEmail.sentDateTime).format('MMM D, YYYY h:mm A')}
                                </div>
                            </div>
                            {/* To recipients */}
                            {selectedEmail.toRecipients?.length > 0 && (
                                <div style={{ fontSize: 11, color: '#797775', marginTop: 6 }}>
                                    To: {selectedEmail.toRecipients.map(r => r.emailAddress?.address || r).join(', ')}
                                </div>
                            )}
                        </div>

                        {/* Tracking panel (SES emails) */}
                        {(selectedEmail.provider === 'ses' || selectedEmail.trackId) && (
                            <TrackingPanel email={selectedEmail} />
                        )}

                        {/* Attachments — Graph emails fetched from /api/outlook/attachments,
                            SES emails use inline metadata (no contentBytes; download is disabled) */}
                        {selectedEmail.hasAttachments && (
                            <AttachmentList items={attachments} loading={attachmentsLoading} />
                        )}

                        {/* Email body */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
                            {activeFolder === 'junkemail' ? (
                                <div
                                    dangerouslySetInnerHTML={{ __html: stripTrackingPixels(emailOpen?.content || selectedEmail.body?.content || '') }}
                                    style={{ fontFamily: 'inherit', fontSize: 14 }}
                                />
                            ) : response?.length > 0 ? (
                                <EmailThread
                                    EmailList={EmailFormList}
                                    replies={response}
                                    userEmail={email}
                                    handleFetchData={handleFetchData}
                                />
                            ) : emailOpen?.messageId && singleEmailReply ? (
                                <ReplyCard
                                    reply={singleEmailReply}
                                    index={0}
                                    minimized={false}
                                    isLast={activeFolder !== 'junkemail'}
                                    userEmail={email}
                                    isFlagged={flaggedSet.has(selectedEmail?.conversationId)}
                                    onReplyClick={handleSingleReply}
                                    onReplyAllClick={handleSingleReplyAll}
                                    onForwardClick={handleSingleForward}
                                    onDelete={handleSingleDelete}
                                    onMarkUnread={handleSingleMarkUnread}
                                    onFlag={handleSingleFlag}
                                />
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
                                    <Spin size="small" />
                                </div>
                            )}
                        </div>

                    </>
                ) : (
                    <EmptyPane label={tab ? '' : 'Select a message'} />
                )}
            </div>

            {/* Compose modal (openWindow) */}
            <Modal
                open={openWindow}
                closeIcon={false}
                footer={null}
                width="90%"
                styles={{ body: { height: '80vh', overflowY: 'auto', padding: '20px', top: 0 } }}
            >
                <EmailForm
                    EmailList={EmailFormList}
                    setTab={(val) => { setTab(val); if (!val) setOutlookDraftEdit(null); }}
                    outlookDraftData={outlookDraftEdit}
                />
            </Modal>

            {/* Reply / Reply All / Forward modal for non-thread emails */}
            {/* destroyOnClose so EmailForm actually unmounts on close — its unmount
                effect auto-saves the draft. Without this the modal just hides and
                the half-written reply is lost. */}
            <Modal
                open={!!noThreadCompose}
                onCancel={() => setNoThreadCompose(null)}
                footer={null}
                width="90%"
                destroyOnClose
                styles={{ body: { height: '80vh', overflowY: 'auto', padding: '20px', top: 0 } }}
            >
                <EmailForm
                    EmailList={EmailFormList}
                    setTab={setTab}
                    replyData={noThreadCompose?.replyData}
                    forwardData={noThreadCompose?.forwardData}
                    handleCloseModal={() => setNoThreadCompose(null)}
                />
            </Modal>
        </div>
    );
};

export default OutlookFolders;
